import React, { useMemo } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EditIcon from '@mui/icons-material/Edit';
import RemoveFromScheduleIcon from '@mui/icons-material/EventBusy';
import { IconButton } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { sortBy } from '@paragon/utilities';

import { showEditScheduleLineGroupDialog } from '../../../actions/dialogActions';
import { removeBundleFromScheduleLineGroup, updatePriorities } from '../../../actions/scheduleLineGroupActions';
import { getStatusesByBundleGuid } from '../../../selectors/bundles';
import { LineScheduleGridRow } from '../../../selectors/lineScheduleGrid';
import { getStatusesByScheduleLineGroupGuid } from '../../../selectors/scheduleLineGroups';
import { useAppDispatch, useAppSelector } from '../../hooks';

const buttonSx = { height: 20, width: 20, ml: 0.5, mr: 0.5 };

interface LineScheduleGridRowActionsProps {
  readonly row: LineScheduleGridRow;
  readonly rows: readonly LineScheduleGridRow[];
}

export function LineScheduleGridRowActions(props: LineScheduleGridRowActionsProps) {
  const {
    row: { rowId, guid, type },
    rows,
  } = props;
  const dispatch = useAppDispatch();
  const statusesByBundleGuid = useAppSelector(getStatusesByBundleGuid);
  const statusesByScheduleLineGroupGuid = useAppSelector(getStatusesByScheduleLineGroupGuid);

  const sortedScheduleLineGroupRows = useMemo(() => {
    const scheduleLineGroupRows = rows.filter((row) => row.type === 'scheduleLineGroup');
    return sortBy(scheduleLineGroupRows, (row) => row.priority);
  }, [rows]);

  const shouldShowUpArrow =
    type === 'scheduleLineGroup' &&
    sortedScheduleLineGroupRows.length > 0 &&
    rowId !== sortedScheduleLineGroupRows.at(0)!.rowId;
  const shouldShowDownArrow =
    type === 'scheduleLineGroup' &&
    sortedScheduleLineGroupRows.length > 0 &&
    rowId !== sortedScheduleLineGroupRows.at(-1)!.rowId;

  function editingIsAllowedForScheduleLineGroupRow() {
    const { row } = props;
    if (row.type !== 'scheduleLineGroup') return false;

    return statusesByScheduleLineGroupGuid[guid]?.every((status) => status === 'Scheduled') ?? false;
  }

  function removingIsAllowedForBundleRow() {
    const { row } = props;
    if (row.type !== 'bundle') return false;
    return statusesByBundleGuid[guid]?.every((status) => status === 'Scheduled') ?? false;
  }

  return (
    <>
      {type === 'scheduleLineGroup' ? (
        <TooltipIconButton
          title={
            editingIsAllowedForScheduleLineGroupRow()
              ? 'Edit schedule line group'
              : 'Editing is only allowed for schedule line groups where all bundles have the status of "Scheduled"'
          }
          sx={buttonSx}
          onClick={(event) => {
            event.stopPropagation();
            dispatch(showEditScheduleLineGroupDialog(guid));
          }}
          disabled={!editingIsAllowedForScheduleLineGroupRow()}
        >
          <EditIcon />
        </TooltipIconButton>
      ) : type === 'bundle' ? (
        <TooltipIconButton
          title={
            removingIsAllowedForBundleRow()
              ? 'Remove from the schedule line group'
              : 'You can only remove a bundle from the schedule line group if its status is "Scheduled"'
          }
          sx={buttonSx}
          onClick={(event) => {
            event.stopPropagation();
            dispatch(removeBundleFromScheduleLineGroup(guid));
          }}
          disabled={!removingIsAllowedForBundleRow()}
        >
          <RemoveFromScheduleIcon />
        </TooltipIconButton>
      ) : null}
      {shouldShowUpArrow ? (
        <TooltipIconButton
          title="Move to higher priority"
          sx={buttonSx}
          onClick={(event) => {
            event.stopPropagation();
            dispatch(updatePriorities(guid, 'up'));
          }}
        >
          <ArrowUpwardIcon />
        </TooltipIconButton>
      ) : (
        <IconButton sx={buttonSx} disabled />
      )}
      {shouldShowDownArrow && (
        <TooltipIconButton
          title="Move to lower priority"
          sx={buttonSx}
          onClick={(event) => {
            event.stopPropagation();
            dispatch(updatePriorities(guid, 'down'));
          }}
        >
          <ArrowDownwardIcon />
        </TooltipIconButton>
      )}
    </>
  );
}
